import { RegisterForm } from './register-form';

export function initConfigurateOrderStep2() : void {
    document.querySelectorAll<HTMLElement>('.js-configurate-order-step-2').forEach($phoneOtpModule => {
        new ConfigurateOrderStep2($phoneOtpModule);
    });
}

class ConfigurateOrderStep2 {
    private readonly $container: HTMLElement;

    // Entity elements
    private $isRequestedForMyEntityRadios: NodeListOf<HTMLInputElement>|null = null;
    private $containersIfIsRequestedForMyEntity: NodeListOf<HTMLElement>|null = null;
    private $containersIfIsNotRequestedForMyEntity: NodeListOf<HTMLElement>|null = null;

    // Holder elements
    private $isHolderRadios: NodeListOf<HTMLInputElement>|null = null;
    private $containerIfIsHolder: HTMLElement|null = null;
    private $containerIfIsNotHolder: HTMLElement|null = null;

    // Face to face addresses elements
    private $faceToFaceContainer: HTMLElement|null = null;
    private $faceToFaceAddressIsSameAsEntityRadios: NodeListOf<HTMLInputElement>|null = null;
    private $faceToFaceAddressInput: HTMLInputElement|null = null;
    private $faceToFaceZipcodeInput: HTMLInputElement|null = null;
    private $faceToFaceCityInput: HTMLInputElement|null = null;

    private faceToFaceOldValues: { address: string, zipcode: string, city: string } = { address: '', zipcode: '', city: '' };

    constructor($container: HTMLElement) {
        this.$container = $container;

        new RegisterForm(this.$container, {
            validateFieldsOnSubmit: false,
            allowPrefilledEntityModifications: false,
            entityIdentifierOptions: {
                allowExistingThirdparties: true,
            },
        });

        // Register elements
        this.getIsRequestedForMyEntityElements();
        this.getHolderElements();
        this.getFaceToFaceAddressesElements();

        // Events
        this.doIsRequestedForMyEntityEvents();
        this.doHolderEvents();
        this.doFaceToFaceAddressesEvents();

        document.addEventListener('toggle_element', e => {
            if ('add-new-user' === e.detail.$element.id) {
                const $input = e.detail.$element.querySelector<HTMLInputElement>('.js-is-holder-create');

                if ($input) {
                    $input.value = e.detail.isVisible ? '1' : '0';
                }

                if (!e.detail.isVisible) {
                    // Empty the add-new-user fields when he is hidden
                    e.detail.$element.querySelectorAll<HTMLInputElement>('input').forEach($inp => {
                        $inp.value = '';
                    });
                }
            }
        });
    }

    /**
     * Get all entity elements
     * 
     * @return {void}
     */
    private getIsRequestedForMyEntityElements() : void {
        this.$isRequestedForMyEntityRadios = this.$container.querySelectorAll<HTMLInputElement>('#certificate_form_requested_for_my_entity input');
        this.$containersIfIsRequestedForMyEntity = this.$container.querySelectorAll<HTMLElement>('.js-show-if-is-requested-for-my-entity');
        this.$containersIfIsNotRequestedForMyEntity = this.$container.querySelectorAll<HTMLElement>('.js-show-if-is-not-requested-for-my-entity');
    }

    /**
     * Do entity events
     * 
     * @return {void}
     */
    private doIsRequestedForMyEntityEvents() : void {
        if (this.$isRequestedForMyEntityRadios?.length) {
            this.onIsRequestedForMyEntityRadioChange();

            this.$isRequestedForMyEntityRadios.forEach($radio => {
                $radio.addEventListener('change', () => this.onIsRequestedForMyEntityRadioChange());
            });
        }
    }

    /**
     * On isRequestedForMyEntityRadios change event
     * 
     * @return {void}
     */
    private onIsRequestedForMyEntityRadioChange() : void {
        let isHolder = false;
        this.$isHolderRadios?.forEach($radio => {
            if ($radio.checked && 'yes' === $radio.value) {
                isHolder = true;
            }
        });

        this.$isRequestedForMyEntityRadios?.forEach($radio => {
            if (!$radio.checked) {
                return;
            }

            if ('yes' === $radio.value) {
                this.$containersIfIsRequestedForMyEntity?.forEach($container => $container.classList.remove('hidden'));
                this.$containersIfIsNotRequestedForMyEntity?.forEach($container => $container.classList.add('hidden'));

                if (isHolder) {
                    this.enableAllFieldsWhenImTheHolder();
                }
                this.fillAndDisableHolderFields();
            } else {
                this.$containersIfIsNotRequestedForMyEntity?.forEach($container => $container.classList.remove('hidden'));
                this.$containersIfIsRequestedForMyEntity?.forEach($container => $container.classList.add('hidden'));

                if (isHolder) {
                    this.disableAllFieldsWhenImNotTheHolder();
                }
                this.emptyAndEnableHolderFields();
            }
        });
    }

    /**
     * Get all holder elements
     * 
     * @return {void}
     */
    private getHolderElements() : void {
        this.$isHolderRadios = this.$container.querySelectorAll<HTMLInputElement>('#certificate_form_is_holder input');
        this.$containerIfIsHolder = this.$container.querySelector<HTMLElement>('.js-show-if-is-holder');
        this.$containerIfIsNotHolder = this.$container.querySelector<HTMLElement>('.js-show-if-is-not-holder');
    }

    /**
     * Do holder events
     * 
     * @return {void}
     */
    private doHolderEvents() : void {
        if (this.$isHolderRadios?.length) {
            this.onIsHolderRadioChange();
            
            this.$isHolderRadios.forEach($isHolderRadio => {
                $isHolderRadio.addEventListener('change', () => this.onIsHolderRadioChange());
            });
        }
    }

    /**
     * On isHolderRadios change event
     * 
     * @return {void}
     */
    private onIsHolderRadioChange() : void {
        let requestedForMyEntity = false;
        this.$isRequestedForMyEntityRadios?.forEach($radio => {
            if ($radio.checked && 'yes' === $radio.value) {
                requestedForMyEntity = true;
            }
        });

        this.$isHolderRadios?.forEach($isHolderRadio => {
            if (!$isHolderRadio.checked) {
                return;
            }

            if ('yes' === $isHolderRadio.value) {
                this.$containerIfIsHolder?.classList.remove('hidden');
                this.$containerIfIsNotHolder?.classList.add('hidden');

                if (requestedForMyEntity) {
                    this.enableAllFieldsWhenImTheHolder();
                }
            } else {
                this.$containerIfIsNotHolder?.classList.remove('hidden');
                this.$containerIfIsHolder?.classList.add('hidden');

                if (requestedForMyEntity) {
                    this.disableAllFieldsWhenImNotTheHolder();
                }
            }
        });
    }

    /**
     * Get all face to face addresses elements
     * 
     * @return {void}
     */
    private getFaceToFaceAddressesElements() : void {
        this.$faceToFaceContainer = this.$container.querySelector<HTMLElement>('.js-face-to-face');
        this.$faceToFaceAddressIsSameAsEntityRadios = this.$container.querySelectorAll<HTMLInputElement>('#certificate_form_face_to_face_address_is_same_as_entity input');
        this.$faceToFaceAddressInput = this.$container.querySelector<HTMLInputElement>('#certificate_form_face_to_face_address');
        this.$faceToFaceZipcodeInput = this.$container.querySelector<HTMLInputElement>('#certificate_form_face_to_face_zipcode');
        this.$faceToFaceCityInput = this.$container.querySelector<HTMLInputElement>('#certificate_form_face_to_face_city');
    }

    /**
     * Do face to face addresses events
     * 
     * @return {void}
     */
    private doFaceToFaceAddressesEvents() : void {
        if (this.$faceToFaceAddressIsSameAsEntityRadios?.length) {
            this.onFaceToFaceAddressIsSameAsEntityChange();

            this.$faceToFaceAddressIsSameAsEntityRadios.forEach($radio => {
                $radio.addEventListener('change', () => this.onFaceToFaceAddressIsSameAsEntityChange());
            });
        }
    }

    /**
     * On faceToFaceAddressIsSameAsEntityRadios change event
     * 
     * @return {void}
     */
    private onFaceToFaceAddressIsSameAsEntityChange() : void {
        this.$faceToFaceAddressIsSameAsEntityRadios?.forEach($radio => {
            if (!$radio.checked) {
                return;
            }

            this.faceToFaceOldValues = {
                address: this.$faceToFaceAddressInput?.value as string,
                zipcode: this.$faceToFaceZipcodeInput?.value as string,
                city: this.$faceToFaceCityInput?.value as string,
            };

            if ('yes' === $radio.value) {
                if (this.$faceToFaceAddressInput) {
                    this.$faceToFaceAddressInput.value = this.$faceToFaceAddressInput.getAttribute('data-entity-value') ?? '';
                    this.$faceToFaceAddressInput.setAttribute('readonly', 'true');
                }

                if (this.$faceToFaceZipcodeInput) {
                    this.$faceToFaceZipcodeInput.value = this.$faceToFaceZipcodeInput.getAttribute('data-entity-value') ?? '';
                    this.$faceToFaceZipcodeInput.setAttribute('readonly', 'true');
                }

                if (this.$faceToFaceCityInput) {
                    this.$faceToFaceCityInput.value = this.$faceToFaceCityInput.getAttribute('data-entity-value') ?? '';
                    this.$faceToFaceCityInput.setAttribute('readonly', 'true');
                }
            } else {
                if (this.$faceToFaceAddressInput) {
                    if (this.$faceToFaceAddressInput.value === this.$faceToFaceAddressInput.getAttribute('data-entity-value')) {
                        this.$faceToFaceAddressInput.value = this.faceToFaceOldValues.address || '';
                    }
                    this.$faceToFaceAddressInput.removeAttribute('readonly');
                }

                if (this.$faceToFaceZipcodeInput) {
                    if (this.$faceToFaceZipcodeInput.value === this.$faceToFaceZipcodeInput.getAttribute('data-entity-value')) {
                        this.$faceToFaceZipcodeInput.value = this.faceToFaceOldValues.zipcode || '';
                    }
                    this.$faceToFaceZipcodeInput.removeAttribute('readonly');
                }

                if (this.$faceToFaceCityInput) {
                    if (this.$faceToFaceCityInput.value === this.$faceToFaceCityInput.getAttribute('data-entity-value')) {
                        this.$faceToFaceCityInput.value = this.faceToFaceOldValues.city || '';
                    }
                    this.$faceToFaceCityInput.removeAttribute('readonly');
                }
            }
        });
    }

    /**
     * Filling of all holder fields with the content of their "data-value" attribute
     * And disabling them
     * 
     * @return {void}
     */
    private fillAndDisableHolderFields() : void {
        if (!this.$containerIfIsHolder) {
            return;
        }

        const $inputs = this.$containerIfIsHolder.querySelectorAll<HTMLInputElement>('input');
        $inputs.forEach($input => {
            if ($input.dataset.value) {
                $input.value = $input.dataset.value;
                $input.setAttribute($input.type === 'button' ? 'disabled' : 'readonly', 'true');
            }
        });
    }

    /**
     * Empty all holder fields
     * And enabling them
     * 
     * @return {void}
     */
    private emptyAndEnableHolderFields() : void {
        if (this.$containerIfIsHolder) {
            const $inputs = this.$containerIfIsHolder.querySelectorAll<HTMLInputElement>('input');
            $inputs.forEach($input => {
                $input.value = '';
                $input.removeAttribute($input.type === 'button' ? 'disabled' : 'readonly');
            });
        }
    }

    /**
     * Enable all the fields that only the holder can edit
     * 
     * @return {void}
     */
    private enableAllFieldsWhenImTheHolder() : void {
        if (this.$faceToFaceContainer) {
            this.$faceToFaceContainer.classList.remove('disabled');
        }
    }

    /**
     * Disable all the fields that only the holder can edit
     * 
     * @return {void}
     */
    private disableAllFieldsWhenImNotTheHolder() : void {
        if (this.$faceToFaceContainer) {
            this.$faceToFaceContainer.classList.add('disabled');
        }
    }
}
